import { AuthUserService, RoleEnum } from 'acl-cloud-lib';
import { MachineOwner, User } from '../../../api/generated';
import { BehaviorSubject, map, Observable, of, take, takeUntil } from 'rxjs';
import { BaseComponent } from '../abstract/base.component';
import { Injectable } from '@angular/core';

@Injectable({providedIn: 'root'})
export class AuthHelperService extends BaseComponent {

  private _userRights: BehaviorSubject<RoleEnum[]> = new BehaviorSubject<RoleEnum[]>([]);
  public userRights$ = this._userRights.asObservable();

  private _fenixRolesWithPriority: Array<{role: string, priority: number}> = [
    {role: "GUEST", priority: 1},
    {role: "OPERATOR", priority: 2},
    {role: "MAINTAINER", priority: 3},
    {role: "ADMIN", priority: 4},
    {role: "SUPER_USER", priority: 5},
  ];

  constructor(
    private readonly _authUserSvc: AuthUserService<User, MachineOwner>
  ) {
    super();

    this._authUserSvc.userDetail$.pipe(takeUntil(this.unSubscriber)).subscribe(user => {
      if (user) {
        this._userRights.next(this._parseRolesFromTags(user.Tags ?? []));
      }
    });
  }

  public userHasOneOfRoles(necessaryRoles: RoleEnum[]): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      this._authUserSvc.userDetail$.pipe(takeUntil(this.unSubscriber)).subscribe(user => {
        let rolIncluded = false;

        if (user) {
          const actualRoles = this._parseRolesFromTags(user.Tags ?? []);
          necessaryRoles.forEach(role => {
            if (actualRoles.includes(role)) {
              rolIncluded = true;
              return;
            }
          });
        }

        observer.next(rolIncluded);
      });
    });
  }

  public userNotHaveOneOfRoles(necessaryRoles: RoleEnum[]): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      this._authUserSvc.userDetail$.pipe(takeUntil(this.unSubscriber)).subscribe(user => {
        let rolIncluded = true;

        if (user) {
          const actualRoles = this._parseRolesFromTags(user.Tags ?? []);
          necessaryRoles.forEach(role => {
            if (actualRoles.includes(role)) {
              rolIncluded = false;
              return;
            }
          });
        }

        observer.next(!rolIncluded);
      });
    });
  }

  public stringToRoleEnum(role: string): RoleEnum {
    return RoleEnum[role as keyof typeof RoleEnum];
  }

  public roleEnumToString(role: RoleEnum): string {
    return RoleEnum[role];
  }

  private _parseRolesFromTags(tags: string[]): RoleEnum[] {
    const stringRightsList = tags.filter((tag) => tag.startsWith('Right:')).map((tag) => tag.replace('Right:', '')) ?? [];
    return stringRightsList.map((right) => this.stringToRoleEnum(right));
  }

  public isFenixActionEnabled(targetRole: string): Observable<boolean> {
    let copiedTarget = targetRole;

    if (targetRole === RoleEnum.USER) {
      copiedTarget = 'GUEST';
    } else if (targetRole === RoleEnum.SUPERADMIN) {
      copiedTarget = 'SUPER_USER';
    }

    const target = this._fenixRolesWithPriority.find(r => r.role === copiedTarget);
    if (!target) {
      console.error(`Role not find within defined roles. ${copiedTarget}`);
      return of(false);
    }

    return this.userRights$.pipe(
      take(1), // Automatically unsubscribes after the first emission
      map(val => {
        let isEnabled: boolean = false;

        val.forEach(r => {
          if (r) {
            
            const fromArr: { role: string, priority: number } | undefined = this._fenixRolesWithPriority.find(rr => {
              let copiedR: string = r;

              if (r === RoleEnum.USER) {
                copiedR = 'GUEST';
              } else if (r === RoleEnum.SUPERADMIN) {
                copiedR = 'SUPER_USER';
              }

              return rr.role === copiedR;
            });

            if (fromArr && fromArr.priority >= target.priority) {
              isEnabled = true;
              return; // Early exit to stop further iteration
            }
          }
        });

        return isEnabled;
      })
    );
  }

}
